/**
 * Performance Tracking Types (shared between web and mobile)
 */

export type PeriodType = 'monthly' | 'quarterly' | 'half_yearly' | 'yearly';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface PerformanceWeights {
  okrProgress: number;
  checkInCompliance: number;
  engagement: number;
  timeliness: number;
}

export const DEFAULT_PERFORMANCE_WEIGHTS: PerformanceWeights = {
  okrProgress: 0.4,
  checkInCompliance: 0.3,
  engagement: 0.2,
  timeliness: 0.1,
};

export interface PerformanceScore {
  overall: number;
  okrProgress: number;
  checkInCompliance: number;
  engagement: number;
  timeliness: number;
}

export interface OKRPerformanceItem {
  okrId: string;
  title: string;
  type: string;
  progress: number;
  status: string;
  contribution: number;
  keyResultsCount: number;
  keyResultsCompleted: number;
}

export interface PerformanceFactor {
  factor: 'okrProgress' | 'checkInCompliance' | 'engagement' | 'timeliness';
  name: string;
  weight: number;
  score: number;
  weightedScore: number;
  description: string;
}

export interface Improvement {
  area: string;
  recommendation: string;
  priority: 'high' | 'medium' | 'low';
  currentScore: number;
  potentialGain: number;
}

export interface PerformanceTrendPoint {
  periodStart: string;
  periodEnd: string;
  periodLabel: string;
  score: number;
}

export interface PerformanceData {
  userId: string;
  userName: string;
  userEmail: string;
  period: {
    type: PeriodType;
    start: string;
    end: string;
    label: string;
  };
  score: PerformanceScore;
  okrs: OKRPerformanceItem[];
  factors: PerformanceFactor[];
  improvements: Improvement[];
  summary: string;
  trend?: PerformanceTrendPoint[];
  snapshotId?: string;
  calculatedAt: string;
}

export interface ScoreDistribution {
  excellent: number;
  good: number;
  needsImprovement: number;
  poor: number;
}

export interface DirectReportPerformance {
  userId: string;
  userName: string;
  userEmail: string;
  score: number;
  trend: 'up' | 'down' | 'stable';
  okrCount: number;
  lastCheckIn?: string;
}

export interface TeamMemberPerformance {
  userId: string;
  userName: string;
  userEmail?: string;
  avatarUrl?: string;
  score: number;
  previousScore?: number;
  okrProgress: number;
  checkInCompliance: number;
  engagement: number;
  okrCount: number;
  okrsCompleted: number;
}

export interface TeamPerformanceSummary {
  managerId: string;
  managerName: string;
  period: {
    type: PeriodType;
    start: string;
    end: string;
    label: string;
  };
  teamSize: number;
  averageScore: number;
  scoreDistribution: ScoreDistribution;
  directReports: DirectReportPerformance[];
  topPerformers: DirectReportPerformance[];
  needsAttention: DirectReportPerformance[];
}

export interface DepartmentPerformanceSummary {
  departmentId: string;
  departmentName: string;
  period: {
    type: PeriodType;
    start: string;
    end: string;
    label: string;
  };
  memberCount: number;
  averageScore: number;
  scoreDistribution: ScoreDistribution;
  trend: 'up' | 'down' | 'stable';
  previousPeriodScore?: number;
}

export interface ManagerPerformanceSummary {
  managerId: string;
  managerName: string;
  teamSize: number;
  teamAverageScore: number;
  trend: 'up' | 'down' | 'stable';
}

export interface OrganizationPerformanceSummary {
  tenantId: string;
  tenantName: string;
  period: {
    type: PeriodType;
    start: string;
    end: string;
    label: string;
  };
  totalEmployees: number;
  activeOKRs: number;
  averageScore: number;
  scoreDistribution: ScoreDistribution;
  departmentBreakdown: DepartmentPerformanceSummary[];
  managerBreakdown: ManagerPerformanceSummary[];
  trend: PerformanceTrendPoint[];
}

export interface PerformanceExportParams {
  type: 'individual' | 'team' | 'department' | 'organization';
  userId?: string;
  departmentId?: string;
  periodType: PeriodType;
  startDate: string;
  endDate: string;
  includeDetails?: boolean;
  includeTrend?: boolean;
}

export interface PerformanceQueryParams {
  periodType?: PeriodType;
  startDate?: string;
  endDate?: string;
  trendPeriods?: number;
}

export interface PerformancePermissions {
  canViewOwn: boolean;
  canViewTeam: boolean;
  canViewOrg: boolean;
  canExport: boolean;
  canConfigureWeights: boolean;
}

export const PERFORMANCE_THRESHOLDS = {
  EXCELLENT_MIN: 90,
  GOOD_MIN: 70,
  NEEDS_IMPROVEMENT_MIN: 50,
  POOR_MAX: 49,
} as const;

export function getScoreCategory(
  score: number
): 'excellent' | 'good' | 'needsImprovement' | 'poor' {
  if (score >= PERFORMANCE_THRESHOLDS.EXCELLENT_MIN) return 'excellent';
  if (score >= PERFORMANCE_THRESHOLDS.GOOD_MIN) return 'good';
  if (score >= PERFORMANCE_THRESHOLDS.NEEDS_IMPROVEMENT_MIN) return 'needsImprovement';
  return 'poor';
}

export function getScoreColor(score: number): string {
  const category = getScoreCategory(score);
  switch (category) {
    case 'excellent':
      return '#10B981';
    case 'good':
      return '#3B82F6';
    case 'needsImprovement':
      return '#F59E0B';
    case 'poor':
      return '#EF4444';
  }
}

export const PERIOD_LABELS: Record<PeriodType, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  yearly: 'Yearly',
};
